import { DriverInfo, PageLayout } from '@/components';
import { TicketEntityIDFieldEnum } from '@/constants';
import {
  useCancelPickupById,
  useGeneratePickupLabel,
  useFetchSinglePickupById,
  useFetchPickupLabel,
} from '@/hooksApi';
import { fetchPickupOrders } from '@/api';
import { useOpenEntityTicketModal, useOrderListPagination } from '@/hooks';
import { usePollingMechanismStore } from '@/stores';
import { ProcessNameEnum } from '@/stores/pollingMechanismStore';
import { getStatusColors } from '@/pages/order-details/components/StatusColors';
import { getStatusActivePickup, triggerDownload, formatWithThousandSeparator, formatPhoneDisplay} from '@/utils';
import { useQueryClient } from '@tanstack/react-query';
import { message, notification } from 'antd';
import { Button } from '@/components/ui/button';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Ticket,
  Download,
  Copy,
  Phone,
  MessageCircle,
  Info,
  MapPin,
  ChevronDown,
} from 'lucide-react';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import { formatDateByDayjs, isValidLatLng } from '@/utils';
import MapComponent from '@/components/Map/MapComponent';

const PickupSingle = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTO = location.pathname.includes('/rto/');
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const [disabled, setDisabled] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'orders'>('details');

  const { mutate: CancelPickupById, isLoading: cancelPickupLoading } = useCancelPickupById();
  const { data: dataSinglePickup, isLoading: loadingSinglePickup } = useFetchSinglePickupById(
    id as string,
    600000,
  );
  const { isLoading: isGeneratingPickupLabel, mutateAsync: generatePickupLabel } =
    useGeneratePickupLabel();
  const { isLoading: isFetchingPickupLabel, mutateAsync: fetchPickupLabel } = useFetchPickupLabel();
  const { startPolling } = usePollingMechanismStore();
  const { openEntityTicketModal } = useOpenEntityTicketModal(TicketEntityIDFieldEnum.PICKUP);

  const response = dataSinglePickup?.object;
  const statusSlug = response?.status?.slug || response?.status?.title || 'new';

  const handleGenerateAndFetchLabel = async () => {
    setDisabled(true);
    await generatePickupLabel(id as string);
    startPolling(
      ProcessNameEnum.GENERATE_LABEL,
      { fileName: 'label', toastTextKey: 'preparing_pickup_label' },
      checkRefetchingPickupLabel,
    );
  };

  const checkRefetchingPickupLabel = async () => {
    try {
      const data = await fetchPickupLabel(id as string);
      if (data.object.status === 'SUCCESS') {
        notification.success({
          message: t('download_ready'),
          description: t('your_file_is_downloading'),
          showProgress: true,
          duration: 5,
        });
        triggerDownload(data.object.file.abs_url, data.object.file.filename);
        setTimeout(() => setDisabled(false), 2000);
        return true;
      }
      if (data.object.status === 'FAILED') {
        notification.error({ message: t('operation_failed_please_try_again'), duration: 5 });
        setDisabled(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Download failed', error);
      setDisabled(false);
      return true;
    }
  };

  const handleCopyId = () => {
    if (response?.platform_code) {
      navigator.clipboard.writeText(response.platform_code);
      message.success(t('copied'));
    }
  };

  // Pipeline steps
  const getStepStatus = (stepIndex: number) => {
    const current = getStatusActivePickup(statusSlug);
    if (stepIndex < current) return 'completed';
    if (stepIndex === current) return 'current';
    return 'pending';
  };

  const steps = [
    { label: t('scheduled'), statusLabel: '' },
    { label: t('collect'), statusLabel: '' },
    { label: t('collected'), statusLabel: '' },
  ];

  // Set status labels based on current step
  const currentStep = getStatusActivePickup(statusSlug);
  steps.forEach((step, i) => {
    if (i < currentStep) step.statusLabel = String(t('completed', { defaultValue: 'Completed' }));
    else if (i === currentStep)
      step.statusLabel = String(t('in_progress_step', { defaultValue: 'In progress' }));
    else step.statusLabel = String(t('pending', { defaultValue: 'Pending' }));
  });

  if (loadingSinglePickup) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='w-8 h-8 border-2 border-[rgb(237,224,74)] border-t-transparent rounded-full animate-spin' />
      </div>
    );
  }

  if (!response) {
    return (
      <PageLayout>
        <div className='flex flex-col items-center justify-center min-h-[400px] gap-4'>
          <p className='text-[15px] text-[rgb(115,123,140)]'>
            {t('pickup_not_found', { defaultValue: 'Pickup not found' })}
          </p>
          <button
            type='button'
            onClick={() => navigate('/pickups')}
            className='flex items-center gap-2 px-4 py-2 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[13px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
          >
            <ArrowLeft className='w-4 h-4' />
            {t('back_to_pickups', { defaultValue: 'Back to Shipments' })}
          </button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* ─── Sub-header: ID + date + status ─── */}
      <div className='flex items-start justify-between mb-5'>
        <div>
          <div className='flex items-center gap-2'>
            <span className='text-xl font-bold tracking-tight'>
              {response?.platform_code ?? '—'}
            </span>
            <button
              type='button'
              onClick={handleCopyId}
              className='p-1 rounded-md hover:bg-sd-muted transition-colors cursor-pointer bg-transparent border-0 text-sd-muted-foreground hover:text-sd-foreground'
            >
              <Copy className='h-4 w-4' strokeWidth={2} />
            </button>
          </div>
          <p className='text-[12px] text-sd-muted-foreground mt-1 m-0'>
            {response?.created_at ? formatDateByDayjs(response.created_at, 'D MMMM YYYY') : ''}
          </p>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <span
            className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap'
            style={{
              backgroundColor:
                statusSlug === 'collected' || statusSlug === 'bulk-collected'
                  ? 'rgba(34,197,94,0.12)'
                  : statusSlug === 'collecting' || statusSlug === 'out-for-collecting'
                    ? 'rgba(245,158,11,0.12)'
                    : statusSlug === 'scheduled'
                      ? 'rgba(59,130,246,0.12)'
                      : 'rgba(168,85,247,0.12)',
              color:
                statusSlug === 'collected' || statusSlug === 'bulk-collected'
                  ? 'rgb(22,163,74)'
                  : statusSlug === 'collecting' || statusSlug === 'out-for-collecting'
                    ? 'rgb(217,119,6)'
                    : statusSlug === 'scheduled'
                      ? 'rgb(37,99,235)'
                      : 'rgb(147,51,234)',
            }}
          >
            {String(
              t(`merchant_title_order_status.${statusSlug}`, {
                defaultValue: response?.status?.title || statusSlug,
              }),
            )}
          </span>
          <button
            onClick={handleGenerateAndFetchLabel}
            disabled={disabled || isFetchingPickupLabel}
            className='flex items-center gap-1.5 h-8 px-3 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[11px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors disabled:opacity-50'
          >
            {disabled || isFetchingPickupLabel ? (
              <span className='h-3 w-3 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
            ) : (
              <Download size={13} />
            )}
            {String(t('download_label'))}
          </button>
          {hasPermission(PermissionName.CREATE_TICKET) && (
            <button
              onClick={() => openEntityTicketModal(response?.platform_code)}
              className='flex items-center gap-1.5 h-8 px-3 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[11px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            >
              <Ticket size={13} />
              {String(t('ticket'))}
            </button>
          )}
        </div>
      </div>

      {/* ─── Tabs: Details | Orders ─── */}
      <div className='flex w-full border-b border-[rgb(232,230,227)] mb-6 mt-4'>
        <button
          onClick={() => setActiveTab('details')}
          className={`px-5 pb-2.5 pt-1 text-[13px] font-semibold border-0 border-b-2 border-solid -mb-px transition-colors cursor-pointer bg-transparent ${
            activeTab === 'details'
              ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]'
              : 'border-b-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]'
          }`}
        >
          {String(t('details', { defaultValue: 'Details' }))}
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-5 pb-2.5 pt-1 text-[13px] font-semibold border-0 border-b-2 border-solid -mb-px transition-colors cursor-pointer bg-transparent ${
            activeTab === 'orders'
              ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]'
              : 'border-b-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]'
          }`}
        >
          {String(t('orders'))}
        </button>
      </div>

      {/* ─── Details Tab ─── */}
      {activeTab === 'details' && (
        <div className='grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start'>
          {/* Left column */}
          <div className='flex flex-col gap-5'>
            {/* TRACKING TIMELINE */}
            <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-6'>
              <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)] block mb-6'>
                {String(t('tracking_timeline', { defaultValue: 'Tracking Timeline' }))}
              </span>
              <div className='flex items-start justify-between'>
                {steps.map((step, index) => {
                  const status = getStepStatus(index);
                  const isLast = index === steps.length - 1;
                  return (
                    <div key={index} className='flex items-start flex-1'>
                      <div className='flex flex-col items-center'>
                        {/* Circle */}
                        <div
                          className='w-6 h-6 rounded-full border-2 flex items-center justify-center'
                          style={{
                            borderColor:
                              status === 'completed'
                                ? 'rgb(237,224,74)'
                                : status === 'current'
                                  ? 'rgb(237,224,74)'
                                  : 'hsl(40,10%,80%)',
                            backgroundColor:
                              status === 'completed'
                                ? 'rgb(237,224,74)'
                                : status === 'current'
                                  ? 'rgba(237,224,74,0.15)'
                                  : status === 'pending'
                                    ? 'hsl(40,10%,96%)'
                                    : 'transparent',
                          }}
                        >
                          {status === 'completed' && (
                            <svg
                              width='10'
                              height='10'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='rgb(57,57,60)'
                              strokeWidth='3'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            >
                              <polyline points='20 6 9 17 4 12' />
                            </svg>
                          )}
                          {status === 'current' && (
                            <div className='w-2.5 h-2.5 rounded-full bg-[rgb(237,224,74)]' />
                          )}
                          {status === 'pending' && (
                            <div className='w-1.5 h-1.5 rounded-full bg-[hsl(40,10%,75%)]' />
                          )}
                        </div>
                        {/* Label */}
                        <span className='text-[12px] font-medium text-[rgb(57,57,60)] mt-2 text-center'>
                          {step.label}
                        </span>
                        <span className='text-[10px] text-[rgb(115,123,140)] mt-0.5 text-center'>
                          {step.statusLabel}
                        </span>
                      </div>
                      {/* Connector line */}
                      {!isLast && (
                        <div
                          className='flex-1 h-0.5 mt-2.5 mx-3'
                          style={{
                            backgroundColor:
                              status === 'completed' ? 'rgb(237,224,74)' : 'rgb(232,230,227)',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* DRIVER */}
            <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
              <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)] block mb-4'>
                {String(t('driver'))}
              </span>
              {response?.driver ? (
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-3'>
                    <div
                      className='w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white text-[13px] font-semibold'
                      style={{
                        backgroundColor: `hsl(${((response.driver.first_name || '').charCodeAt(0) * 37) % 360}, 55%, 45%)`,
                      }}
                    >
                      {`${(response.driver.first_name ?? '')[0] || ''}${(response.driver.last_name ?? '')[0] || ''}`.toUpperCase()}
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-[14px] font-semibold text-[rgb(57,57,60)]'>
                        {`${response.driver.first_name || ''} ${response.driver.last_name || ''}`.trim()}
                      </span>
                      <span className='text-[12px] text-[rgb(115,123,140)]' dir='ltr'>
                        {formatPhoneDisplay(response.driver.phone)}
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    {response.driver.phone && (
                      <a
                        href={`tel:${response.driver.phone}`}
                        className='w-9 h-9 rounded-full border border-solid border-[rgb(232,230,227)] bg-white flex items-center justify-center hover:bg-[rgb(245,245,243)] transition-colors'
                      >
                        <Phone size={16} className='text-[rgb(57,57,60)]' />
                      </a>
                    )}
                    {response.driver.phone && (
                      <a
                        href={`https://wa.me/${response.driver.phone}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='w-9 h-9 rounded-full border border-solid border-[rgb(232,230,227)] bg-white flex items-center justify-center hover:bg-[rgb(245,245,243)] transition-colors'
                      >
                        <MessageCircle size={16} className='text-[rgb(57,57,60)]' />
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className='flex items-center gap-3 py-2'>
                  <div className='w-10 h-10 rounded-full bg-[rgb(245,245,243)] flex items-center justify-center'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='rgb(115,123,140)'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
                      <circle cx='12' cy='7' r='4' />
                    </svg>
                  </div>
                  <span className='text-sm text-[rgb(115,123,140)]'>
                    {String(
                      t('order_assign', {
                        defaultValue: 'The order will be assigned to a driver when scheduled.',
                      }),
                    )}
                  </span>
                </div>
              )}
            </section>
          </div>

          {/* Right column — sidebar */}
          <div className='flex flex-col gap-5 pb-8'>
            {/* PAYMENT SUMMARY — above map */}
            {/* Payment summary — only for pickups, not RTO (returns don't charge merchant) */}
            {!isRTO && (
              <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
                <div className='flex items-center gap-2 mb-4'>
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='rgb(115,123,140)'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <rect x='2' y='5' width='20' height='14' rx='2' />
                    <line x1='2' y1='10' x2='22' y2='10' />
                  </svg>
                  <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
                    {String(t('payment_summary', { defaultValue: 'Payment Summary' }))}
                  </span>
                </div>
                <div className='divide-y divide-[rgb(232,230,227)]'>
                  <div className='flex justify-between py-2.5'>
                    <span className='text-sm text-[rgb(115,123,140)]'>
                      {String(t('method', { defaultValue: 'Method' }))}
                    </span>
                    <span className='text-sm font-medium text-[rgb(57,57,60)]'>COD</span>
                  </div>
                  <div className='flex justify-between py-2.5'>
                    <span className='text-sm text-[rgb(115,123,140)]'>
                      {String(t('order_value'))}
                    </span>
                    <span className='text-sm font-medium text-[rgb(57,57,60)]'>
                      {formatWithThousandSeparator(Number(response?.order_value) || 0)}{' '}
                      {String(t('iqd'))}
                    </span>
                  </div>
                  <div className='flex justify-between py-2.5'>
                    <span className='text-sm text-[rgb(115,123,140)]'>
                      {String(t('boxy_shipment_fee'))}
                    </span>
                    <span className='text-sm font-medium text-[rgb(57,57,60)]'>
                      {formatWithThousandSeparator(Number(response?.shipment_fee) || 0)}{' '}
                      {String(t('iqd'))}
                    </span>
                  </div>
                  <div className='flex justify-between py-2.5'>
                    <span className='text-sm font-bold text-[rgb(57,57,60)]'>
                      {String(t('total'))}
                    </span>
                    <span className='text-sm font-bold text-[rgb(57,57,60)]'>
                      {formatWithThousandSeparator(Number(response?.total_price) || 0)}{' '}
                      {String(t('iqd'))}
                    </span>
                  </div>
                </div>
                <div className='mt-4 p-3 rounded-xl bg-[rgba(59,130,246,0.05)] border border-solid border-[rgba(59,130,246,0.15)]'>
                  <div className='flex items-start gap-2'>
                    <Info size={14} className='text-[rgb(59,130,246)] shrink-0 mt-0.5' />
                    <span className='text-[11px] text-[rgb(59,130,246)] leading-relaxed'>
                      {String(
                        t('boxy_charges_delivery_info', {
                          amount: formatWithThousandSeparator(Number(response?.shipment_fee) || 0),
                          currency: String(t('iqd')),
                          defaultValue: `Boxy charges ${formatWithThousandSeparator(Number(response?.shipment_fee) || 0)} IQD for delivery. Charges may vary if order size was inaccurate.`,
                        }),
                      )}
                    </span>
                  </div>
                </div>
              </section>
            )}
            {/* MAP — below payment summary */}
            {response?.task &&
              isValidLatLng(Number(response.task.lat), Number(response.task.lng)) && (
                <section className='bg-white rounded-2xl border border-[rgb(232,230,227)] overflow-hidden'>
                  <div className='p-4 pb-0'>
                    <div className='flex items-center gap-2 mb-2'>
                      <MapPin size={14} className='text-[rgb(115,123,140)]' />
                      <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
                        {String(t('location', { defaultValue: 'Location' }))}
                      </span>
                    </div>
                  </div>
                  <div style={{ height: 220 }}>
                    <MapComponent
                      targetLocation={{
                        latitude: Number(response.task.lat),
                        longitude: Number(response.task.lng),
                      }}
                      driverLocation={(() => {
                        const taskData = response.task as any;
                        const driverPos = taskData?.task_list?.driver?.last_position;
                        return driverPos &&
                          isValidLatLng(Number(driverPos.lat), Number(driverPos.lng))
                          ? { latitude: Number(driverPos.lat), longitude: Number(driverPos.lng) }
                          : undefined;
                      })()}
                    />
                  </div>
                </section>
              )}
          </div>
        </div>
      )}

      {/* ─── Orders Tab ─── */}
      {activeTab === 'orders' && <PickupOrdersList pickupUid={id as string} />}
    </PageLayout>
  );
};

/* ─── Pickup Orders List sub-component ─── */
const PICKUP_ORDERS_PAGE_SIZE = 25;

function PickupOrdersList({ pickupUid }: { pickupUid: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Server-side load-more (25/batch). The inner list was previously unpaginated,
  // so a 400+ order pickup rendered every row at once (dashboard perf issue).
  const fetcher = useCallback(
    async (page: number) => {
      const res = await fetchPickupOrders(pickupUid, page, PICKUP_ORDERS_PAGE_SIZE);
      const obj = res?.object ?? {};
      const items = Array.isArray(obj) ? obj : (obj.items ?? []);
      return {
        items,
        page: obj.page ?? page,
        total: obj.total ?? items.length,
        pages: obj.pages ?? 1,
      };
    },
    [pickupUid],
  );

  const {
    items: orderList,
    total,
    isLoading,
    loadingMore,
    hasMore,
    loadMore,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useOrderListPagination<any>(fetcher);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-10'>
        <div className='w-6 h-6 border-2 border-[rgb(237,224,74)] border-t-transparent rounded-full animate-spin' />
      </div>
    );
  }

  if (orderList.length === 0) {
    return (
      <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-8 text-center'>
        <span className='text-sm text-[rgb(115,123,140)]'>
          {String(t('no_orders_in_pickup', { defaultValue: 'No orders in this pickup' }))}
        </span>
      </div>
    );
  }

  const getStatusColor = (slug: string) => getStatusColors(slug);

  return (
    <div>
      <div className='flex items-center justify-between mb-3'>
        <span className='text-[12px] text-[rgb(115,123,140)]'>
          {total} {String(t('orders'))}
        </span>
      </div>

      {/* Table */}
      <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] overflow-hidden'>
        {/* Header */}
        <div className='grid grid-cols-[1fr_1fr_1fr_120px] gap-4 px-5 py-3 border-b border-[rgb(232,230,227)]'>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('order'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('drop_off_location'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('driver'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('status'))}
          </span>
        </div>

        {/* Rows */}
        {orderList.map((order: any) => {
          const statusSlug = order.status?.slug || '';
          const statusColor = getStatusColor(statusSlug);
          const driverName = order.driver?.first_name
            ? `${order.driver.first_name} ${order.driver.last_name || ''}`.trim()
            : '';
          const driverInitials = driverName
            ? driverName
                .split(' ')
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2)
            : '';

          return (
            <button
              key={order.uid || order.id}
              type='button'
              onClick={() => navigate(`/orders/${order.uid}`)}
              className='grid grid-cols-[1fr_1fr_1fr_120px] gap-4 px-5 py-3.5 text-start cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors w-full border-b border-[rgb(232,230,227)] last:border-b-0 bg-transparent border-0 border-solid'
              style={{ borderBottomWidth: 1, borderBottomColor: 'rgb(232,230,227)' }}
            >
              {/* Order ID + date */}
              <div className='flex flex-col gap-0.5'>
                <div className='flex items-center gap-1.5'>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: 'rgb(57,57,60)',
                    }}
                  >
                    {order.platform_code ?? '—'}
                  </span>
                  <Copy
                    size={12}
                    className='text-gray-400 hover:text-gray-600 shrink-0'
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(order.platform_code ?? '');
                      message.success(String(t('copied')));
                    }}
                  />
                </div>
                <span style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}>
                  {formatDateByDayjs(order.created_at, 'DD MMM YYYY')}
                </span>
              </div>

              {/* Drop-off location */}
              <div className='flex items-center gap-1.5'>
                <MapPin size={14} className='text-[rgb(115,123,140)] shrink-0' />
                <span className='text-[12px] text-[rgb(115,123,140)] truncate'>
                  {order.address_text || order.region?.area_name || '—'}
                </span>
              </div>

              {/* Driver */}
              <div className='flex items-center gap-2'>
                {driverName ? (
                  <>
                    <div
                      className='w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0'
                      style={{
                        backgroundColor: `hsl(${(driverName.charCodeAt(0) * 37) % 360}, 55%, 45%)`,
                      }}
                    >
                      {driverInitials}
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-[12px] font-medium text-[rgb(57,57,60)]'>
                        {driverName}
                      </span>
                      {order.driver?.phone && (
                        <span className='text-[11px] text-[rgb(115,123,140)]' dir='ltr'>
                          {formatPhoneDisplay(order.driver.phone)}
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <span className='text-[12px] text-[rgb(115,123,140)]'>—</span>
                )}
              </div>

              {/* Status */}
              <div className='flex items-center'>
                <span
                  className='inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap'
                  style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                >
                  {order.status?.merchant_title ||
                    String(t(`merchant_title_order_status.${statusSlug}`))}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {hasMore ? (
        <div className='mt-4 flex justify-center'>
          <Button
            variant='default'
            onClick={loadMore}
            disabled={loadingMore}
            className='gap-2 px-5 font-semibold shadow-sm'
          >
            {loadingMore ? (
              <span className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
            ) : (
              <ChevronDown className='h-4 w-4' />
            )}
            {String(t('load_more', { defaultValue: 'Load more' }))}
            <span className='text-xs font-normal opacity-70'>
              {orderList.length}/{total}
            </span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default PickupSingle;
